import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Zap, Check, ArrowRight, Crown, Shield, Star } from 'lucide-react';

const AnimatedNumber: React.FC<{ 
  value: number; 
  duration?: number;
  style?: React.CSSProperties;
}> = ({ value, duration = 400, style }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValueRef = useRef(value);
  const animationRef = useRef<number>();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousValueRef.current = value;
      return;
    }

    const endValue = value;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Start from current displayed value to prevent jumps on rapid toggling
    const startValue = displayValue;
    previousValueRef.current = displayValue;
    
    if (startValue === endValue) return;

    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (endValue - startValue) * easeOut;
      
      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = endValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  return (
    <span 
      style={{
        ...style,
        display: 'inline-block',
      }}
    >
      {Math.floor(displayValue)}
    </span>
  );
};
import styles from './Pricing.module.css';
import Link from 'next/link';
import parse from 'html-react-parser'
import { PricingPlanType } from 'types/enums';
import PricingToggle from '@/components/Billing/PricingToggle';
import {
  FREE_APPLICATION_CREDITS_CTA,
  FREE_TRIAL_APPLICATION_CREDITS,
} from '@src/config/freeTrial';
import {
  ELITE_APPLICATION_CREDITS,
  PROFESSIONAL_APPLICATION_CREDITS,
  ULTIMATE_APPLICATION_CREDITS,
} from '@src/config/planCredits';
import { lifeShackProductFacts } from '@src/config/productFacts';

const HOME_PRICING_CTA_LABEL = 'START FREE';


interface Feature {
  text: string;
  priority?: boolean;
  description?: string;
}

interface PricingTierProps {
  name: string;
  price: string | number;
  features: Feature[];
  highlight?: boolean;
  popular?: boolean;
  buttonText: string;
  home?: boolean;
  priceType?: 'monthly' | 'quarterly';
  onClick?: () => void;
  tier?: string;
  tagline?: string;
  accentColor?: string;
  freeTrial?: boolean;
  onboarding?: boolean;
}

const PricingTier: React.FC<PricingTierProps> = ({ 
  name, 
  price, 
  features, 
  highlight, 
  popular, 
  buttonText, 
  home,
  freeTrial,
  onboarding = false,
  priceType = 'monthly',
  onClick,
  tier = 'Standard',
  tagline = 'Perfect for getting started',
  accentColor = 'purple-600'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  const displayPrice = priceType === 'quarterly' ? (numericPrice * 0.8).toFixed(2) : numericPrice.toFixed(2);
  const billedAmount = priceType === 'quarterly' ? (numericPrice * 0.8 * 3).toFixed(2) : null;
  const priceWhole = Math.floor(parseFloat(displayPrice));
  const priceDecimal = (parseFloat(displayPrice) % 1).toFixed(2).substring(1);

  const Wrap = useMemo(() => {
    return !home 
      ? ({ children }: { children: React.ReactNode }) => (
          <div 
            className={onboarding ? 'h-full' : 'h-full cursor-pointer'}
            onClick={onboarding ? undefined : onClick}
          >
            {children}
          </div>
        )
      : ({ children }: { children: React.ReactNode }) => (
          <Link href="/signup">
            <a className="h-full block">{children}</a>
          </Link>
        );
  }, [home, onboarding, onClick]);

  const checkoutNoticeId = `onboarding-checkout-${tier.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div
      data-pricing-tier={onboarding ? tier.toLowerCase() : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative transition-all duration-200 h-full ${
        popular ? 'md:scale-105 md:-translate-y-4' : ''
      } ${isHovered && !popular ? 'scale-[1.02] -translate-y-2' : ''}`}
    >
            {popular && (
              <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', zIndex: 30 }}>
                <div className="relative">
                  <div 
                    className="absolute inset-0 rounded-full blur-md opacity-70"
                    style={{
                      background: 'linear-gradient(to right, #facc15, #eab308, #facc15)'
                    }}
                  />
                  <div 
                    className="relative px-4 py-2 rounded-full shadow-xl flex items-center gap-2"
                    style={{
                      background: 'linear-gradient(to bottom right, #facc15, #eab308, #ca8a04)',
                      border: '1px solid rgba(253, 224, 71, 0.5)'
                    }}
                  >
                    <Crown className="w-4 h-4" style={{ color: '#713f12', fill: '#713f12' }} />
                    <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#713f12' }}>Most Popular</span>
                  </div>
                </div>
              </div>
            )}      
      {/* Sophisticated Glow Layer */}
      <div 
        className={`absolute -inset-[1px] rounded-lg opacity-0 blur-2xl transition-opacity duration-200 ${
          isHovered || popular ? 'opacity-30' : ''
        }`}
        style={{
          background: 'linear-gradient(to bottom right, rgb(147 51 234), rgb(168 85 247), rgb(147 51 234))'
        } as React.CSSProperties}
      />

      {/* Glass Border with Gradient */}
      <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-br from-white via-purple-50/50 to-white opacity-60 transition-opacity duration-500" />

      {/* Main Card */}
      <div 
        className={`relative bg-white/95 backdrop-blur-2xl rounded-lg overflow-hidden border border-[#e5e7eb] transition-all duration-200 h-full ${
          isHovered ? 'shadow-lg shadow-purple-500/20' : 'shadow-sm'
        }`}
      >
        {/* Top Accent Bar with Shimmer */}
        <div className="relative h-1.5 w-full overflow-hidden">
          <div 
            className="absolute inset-0 bg-gradient-to-r from-transparent to-transparent"
            style={{
              backgroundImage: `linear-gradient(to right, transparent, rgb(147 51 234), transparent)`
            }}
          />
          <div 
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-60 transition-transform duration-1000 ${
              isHovered ? '-translate-x-full animate-shimmer' : 'translate-x-full'
            }`}
            style={{
              animation: isHovered ? 'shimmer 2s infinite' : 'none',
            }}
          />
        </div>

        

        <Wrap>
          <div className="p-8 flex flex-col h-full">
            {/* Plan Name */}
            <div className={styles.topContainer}>
              <h3 className="text-4xl font-bold text-gray-900 mb-0 tracking-tight" style={{ marginTop: '0 !important' }}>
                {name}
              </h3>
              <p className={`${styles.tagline} text-gray-600 mb-0 leading-relaxed`}>
                {tagline}
              </p>

              <div>
              <div className="mb-3 relative">
                <div className="flex items-start gap-1 mb-2">
                  <span className="text-2xl font-bold text-gray-900 mt-2">$</span>
                    <AnimatedNumber 
                      value={parseFloat(displayPrice)}
                      style={{
                        color: 'rgb(147, 51, 234)',
                        fontSize: '60px',
                        position: 'relative',
                        top: '3px',
                        fontWeight: '600'
                      }}
                    />
                  <div className="flex flex-col mt-2" style={{ position: "relative", left: "-7px"}}>
                    <span className="text-3xl font-bold text-gray-900">
                      {priceDecimal}                    <span className="text-sm text-gray-500 font-medium">
                      {onboarding && freeTrial ? ' / trial' : '/ month'}
                    </span>
                    </span>
                  </div>
                </div>
                {freeTrial && (
                    <div 
                    className="inline-flex items-center justify-center mt-2 px-4 py-2 rounded-full"
                    style={{ backgroundColor: 'rgb(147 51 234 / 0.1)', display: 'flex', alignItems: 'center' }}
                    >
                      <div style={{ position: "relative", top: "2px" }} className="text-sm font-semibold text-gray-700">
                        No Credit Card Required
                      </div>
                    </div>
                )}                
                {!freeTrial && billedAmount && (
                    <div 
                    className="inline-flex items-center justify-center mt-2 px-4 py-2 rounded-full"
                    style={{ backgroundColor: 'rgb(147 51 234 / 0.1)', display: 'flex', alignItems: 'center' }}
                    >
                      <div style={{ position: "relative", top: "2px" }} className="text-sm font-semibold text-gray-700">
                        ${billedAmount} billed quarterly
                      </div>
                    </div>
                )}
                {!freeTrial && !billedAmount && (
                    <div 
                    className="inline-flex items-center justify-center mt-2 px-4 py-2 rounded-full"
                    style={{ backgroundColor: 'rgb(147 51 234 / 0.1)', display: 'flex', alignItems: 'center' }}
                    >
                      <div style={{ position: "relative", top: "2px" }} className="text-sm font-semibold text-gray-700">
                        Renews each month
                      </div>
                    </div>
                )}                
              </div>

                {/* Refined Divider */}
                <div className="relative mb-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Features List */}
            <ul className="space-y-3 mb-6 flex-grow">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-4 group/item">
                  <div 
                    className={`flex-shrink-0 w-7 h-7 rounded-xl flex items-center justify-center mt-0.5 transition-all duration-300 ${
                      feature.priority
                        ? 'bg-gradient-to-br from-purple-600 to-purple-800 shadow-lg shadow-purple-500/50'
                        : ''
                    }`}
                    style={!feature.priority ? {
                      backgroundColor: 'rgb(147 51 234 / 0.1)',
                      transition: 'background-color 0.3s'
                    } : {}}
                    onMouseEnter={!feature.priority ? (e) => {
                      e.currentTarget.style.backgroundColor = 'rgb(147 51 234 / 0.2)';
                    } : undefined}
                    onMouseLeave={!feature.priority ? (e) => {
                      e.currentTarget.style.backgroundColor = 'rgb(147 51 234 / 0.1)';
                    } : undefined}
                  >
                    {feature.priority ? (
                      <Zap className="w-4 h-4 text-white" fill="white" />
                    ) : (
                      <Check className="w-4 h-4" style={{ color: 'rgb(147 51 234)' }} strokeWidth={3} />
                    )}
                  </div>
                  <span style={{ paddingTop: '3px' }} className={`text-gray-700 leading-relaxed flex-1 ${
                    feature.priority ? 'font-semibold' : ''
                  }`}>
                    {parse(feature.text)}
                    {feature.priority && feature.description && (
                      <div className="text-sm text-gray-600 font-normal mt-1">
                        {feature.description}
                      </div>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {onboarding && (
              <p id={checkoutNoticeId} className={styles.checkoutNotice} data-checkout-notice>
                Opens secure checkout. Completing checkout activates Auto Apply: applications are sent automatically,
                without review before submission. You can pause Auto Apply or block companies anytime.
              </p>
            )}

            {/* Luxury CTA Button */}
            <button 
              type="button"
              onClick={onboarding ? onClick : undefined}
              aria-label={home ? FREE_APPLICATION_CREDITS_CTA : undefined}
              aria-describedby={onboarding ? checkoutNoticeId : undefined}
              className={`${onboarding ? styles.onboardingAction : ''} group/btn relative w-full py-5 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-500 mt-auto ${
              highlight || popular
                ? 'bg-gray-800 text-white shadow-xl hover:shadow-2xl'
                : 'bg-gray-800 text-white shadow-xl hover:shadow-2xl'
              }`}
            >
              <span className="relative z-10 flex items-center justify-center gap-2 px-2">
                <span className={onboarding ? styles.actionLabel : 'whitespace-nowrap'}>{buttonText}</span>
                <ArrowRight className="h-5 w-5 shrink-0 group-hover/btn:translate-x-1 transition-transform duration-300" />
              </span>
              
              {/* Button Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000" />
              
              {/* Subtle Inner Glow */}
              <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-white/10 to-transparent" />
            </button>
          </div>
        </Wrap>

        {/* Bottom Reflection Effect */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-purple-50/50 to-transparent opacity-40 pointer-events-none" />
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

const PricingSection = ({ home, onClose, handleClick, freeTrial, handlePriceType, priceType, onboarding = false }: {
  home: boolean;
  onClose?: () => void;
  handleClick: (plan: PricingPlanType) => void;
  freeTrial: boolean;
  handlePriceType: (type: string) => void;
  priceType: 'monthly' | 'quarterly';
  onboarding?: boolean;
}) => {

  const tiers: Array<{
    name: string;
    price: string;
    tagline: string;
    features: Array<{ text: string; priority?: boolean }>;
    buttonText: string;
    onClick: (() => void) | null;
    popular?: boolean;
    highlight?: boolean;
    freeTrial?: boolean;
  }> = [
    {
      name: "Professional",
      price: String(lifeShackProductFacts.plans.professional.monthlyPriceUsd),
      tagline: "Perfect for getting started",
      features: [
        { text: `<b>${PROFESSIONAL_APPLICATION_CREDITS} applications / month</b> ` },
        { text: "Custom tailored AI cover letters" },
      ],
      buttonText: home ? HOME_PRICING_CTA_LABEL : onboarding ? 'Continue with Professional' : 'GET STARTED',
      onClick: !home ? () => handleClick(priceType === 'quarterly' ? PricingPlanType.ProfessionalQuarterly : PricingPlanType.Professional) : null
    },
    {
      name: "Ultimate",
      price: String(lifeShackProductFacts.plans.ultimate.monthlyPriceUsd),
      tagline: "Made for the serious job seeker",
      features: [
        { text: `<b>${ULTIMATE_APPLICATION_CREDITS} applications / month</b>` },
        { text: "Custom tailored AI cover letters" },
        { text: "Custom tailored AI resumes" },
        { 
            text: "PriorityApply",
            priority: true,
          //   description: "Instantly apply to the latest job postings and stay ahead of the competition."
        },
      ],
      buttonText: home ? HOME_PRICING_CTA_LABEL : onboarding ? 'Continue with Ultimate' : 'GET STARTED',
      onClick: !home ? () => handleClick(priceType === 'quarterly' ? PricingPlanType.UltimateQuarterly : PricingPlanType.Ultimate) : null
    },
    {
      name: "Elite",
      price: String(lifeShackProductFacts.plans.elite.monthlyPriceUsd),
      popular: true,
      highlight: true,
      tagline: "Maximum impact for high-volume searches",
      features: [
        { text: `<b>${ELITE_APPLICATION_CREDITS} applications / month</b>` },
        { text: "Custom tailored AI cover letters" },
        { text: "Custom tailored AI resumes" },
        { text: "Priority support" },
        { text: "Custom AI training" },
        { 
            text: "PriorityApply",
            priority: true,
          //   description: "Instantly apply to the latest job postings and stay ahead of the competition."
        },
      ],
      buttonText: home ? HOME_PRICING_CTA_LABEL : onboarding ? 'Continue with Elite' : 'GET STARTED',
      onClick: !home ? () => handleClick(priceType === 'quarterly' ? PricingPlanType.EliteQuarterly : PricingPlanType.Elite) : null
    }
  ];

  if (freeTrial) {
    tiers.unshift({
      name: "Free Trial",
      price: "0",
      tagline: onboarding ? 'Start with free application credits' : 'Try before you commit',
      features: [
        { text: `<b>${FREE_TRIAL_APPLICATION_CREDITS} applications</b>` },
        { text: "<b>No credit card required</b>" },
      ],
      freeTrial: true,
      buttonText: home ? HOME_PRICING_CTA_LABEL : onboarding ? `Continue with ${FREE_TRIAL_APPLICATION_CREDITS} free credits` : 'GET STARTED',
      onClick: !home ? () => handleClick(PricingPlanType.Trial) : null
    })
  }

  return (
    <div className={`${freeTrial ? styles.freeTrial : ''} ${styles.container} ${!home ? styles.inApp : ''} ${onboarding ? styles.onboarding : ''}`} id="pricing">
      <div className={styles.header}>
        {onClose && (
          <button 
            type="button"
            onClick={onClose} 
            aria-label={onboarding ? 'Back to setup' : undefined}
            className={styles.backButton}
            style={{
              background: 'none', 
              border: 'none', 
              fontSize: '24px', 
              cursor: 'pointer',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
            }}
          >
            ← <span style={{ fontSize: '18px' }}>Back</span>
          </button>
        )}
        {onboarding ? <>
          <h1 className={styles.onboardingTitle}>Choose how to get started</h1>
          <p className={styles.onboardingIntro}>
            {freeTrial
              ? `Continue with ${FREE_TRIAL_APPLICATION_CREDITS} free application credits—no credit card required—or choose a paid plan.`
              : 'Your setup is saved. Choose the paid plan that fits your search, or go back to review your details.'}
          </p>
          <p className={styles.checkoutIntro}>Nothing starts on this screen. You’ll confirm your selection in checkout.</p>
        </> : <>
          <div style={{ fontWeight: 600, fontSize: '4rem', marginTop: '40px', lineHeight: '1.1' }} className={`${styles.pricingTitle} leading-tight md:leading-normal`}>Supercharge Your Search</div>
          <div style={{ fontWeight: 400, fontSize: '1.5rem', marginTop: '20px', lineHeight: '1.4' }} className="leading-relaxed">
            Choose the plan that fits your search. Start with 5 free application credits—no credit card required.
          </div>
        </>}
      </div>
      <PricingToggle onUpdate={(planType) => handlePriceType(planType)} minPadding={false} />
      <div className={styles.grid}>
        {tiers.map((tier) => (
          <PricingTier tier={tier.name} priceType={priceType} key={tier.name} {...tier} home={home} onboarding={onboarding} />
        ))}
      </div>
      <TrustIcons />      
    </div>      
  );
};

function TrustIcons() {
  return (
    <div className="flex flex-col items-center mt-12">
      {/* Mobile: icons col + text col side by side */}
      <div className="flex md:hidden justify-center text-sm">
        <div className="flex flex-col gap-3 items-center">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Check className="w-5 h-5 text-purple-600" strokeWidth={3} />
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Star className="w-5 h-5 text-purple-600" fill="currentColor" />
          </div>
        </div>
        <div className="flex flex-col gap-3 justify-center ml-3">
          <span className="font-medium text-gray-600 h-10 flex items-center">14-day money-back guarantee</span>
          <span className="font-medium text-gray-600 h-10 flex items-center">Cancel anytime</span>
          <span className="font-medium text-gray-600 h-10 flex items-center">Secure & encrypted</span>
        </div>
      </div>

      {/* Desktop: horizontal row */}
      <div className="hidden md:flex flex-wrap justify-center text-sm">
        <div className="flex items-center gap-3 text-gray-600 w-64 justify-center">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center" style={{ position: "relative", top: "-2px", marginRight: "-5px"}}>
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <span className="font-medium">14-day money-back guarantee</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600 w-64 justify-center">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center" style={{ position: "relative", top: "-2px", marginRight: "-5px"}}>
            <Check className="w-5 h-5 text-purple-600" strokeWidth={3} />
          </div>
          <span className="font-medium">Cancel anytime</span>
        </div>
        <div className="flex items-center gap-3 text-gray-600 w-64 justify-center">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center" style={{ position: "relative", top: "-2px", marginRight: "-5px"}}>
            <Star className="w-5 h-5 text-purple-600" fill="currentColor" />
          </div>
          <span className="font-medium">Secure & encrypted</span>
        </div>
      </div>
    </div>      
  )
}

export default PricingSection;
